import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ShieldCheck, Truck, RotateCcw, Minus, Plus } from "lucide-react";
import { resolveImg } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then((r) => setProduct(r.data))
      .catch(() => { toast.error("Product not found"); nav("/products"); });
  }, [id]);

  if (!product) return <div className="container-app px-6 py-20 text-center text-[#595959]">Loading…</div>;

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`Added ${qty} × ${product.name}`);
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    nav("/cart");
  };

  return (
    <div className="container-app px-6 md:px-10 py-10">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="rounded-3xl overflow-hidden bg-white border border-gray-100">
          <img data-testid="product-image" src={resolveImg(product.image_url)} alt={product.name}
               className="w-full h-full object-cover aspect-square"/>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#638971] font-bold">{product.category}</div>
          <h1 data-testid="product-name" className="mt-2 font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">
            {product.name}
          </h1>
          <div data-testid="product-price" className="mt-4 font-heading text-3xl font-bold text-[#2C4035]">
            ${product.price.toFixed(2)}
          </div>
          <p className="mt-6 text-[#595959] leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-3">
            <div className="inline-flex items-center rounded-full border border-gray-200 bg-white">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 grid place-items-center hover:bg-gray-50 rounded-l-full">
                <Minus size={14}/>
              </button>
              <div data-testid="qty-value" className="px-4 font-semibold">{qty}</div>
              <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)}
                      className="w-10 h-10 grid place-items-center hover:bg-gray-50 rounded-r-full">
                <Plus size={14}/>
              </button>
            </div>
            <Button data-testid="add-to-cart-detail-btn" onClick={handleAdd} size="lg"
                    className="rounded-full bg-[#638971] hover:bg-[#4F705B] text-white px-7">
              Add to cart
            </Button>
            <Button data-testid="buy-now-btn" onClick={handleBuyNow} size="lg" variant="outline"
                    className="rounded-full border-[#2C4035] text-[#2C4035] hover:bg-[#2C4035] hover:text-white px-7">
              Buy Now
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4">
            {[
              { icon: ShieldCheck, label: "Quality Verified" },
              { icon: Truck, label: "Fast Shipping" },
              { icon: RotateCcw, label: "30-day returns" },
            ].map((f, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4 rounded-xl bg-white border border-gray-100">
                <f.icon size={20} className="text-[#638971] mb-2"/>
                <div className="text-xs font-medium text-[#2C4035]">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
